import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useNotifications } from '../../contexts/NotificationContext'
import { useHaptics } from '../../utils/haptics'
import { usePublicSettings, useMoney } from '../../hooks/usePublicSettings'
import { addressLine, formatYmd, callOutCreditText, pricedForText } from './bookingApi'
import { PaymentInstructions } from './BookingNotices'

// Icons
function CheckCircleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <path d="M9 11l3 3L22 4"/>
    </svg>
  )
}

function CalendarIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <path d="M16 2v4M8 2v4M3 10h18"/>
    </svg>
  )
}

function ClockIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 6v6l4 2"/>
    </svg>
  )
}

function MapPinIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  )
}

function PhoneIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  )
}

function MessageCircleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  )
}

function ShareIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
      <polyline points="16,6 12,2 8,6"/>
      <line x1="12" y1="2" x2="12" y2="15"/>
    </svg>
  )
}

function DownloadIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7,10 12,15 17,10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  )
}

export default function BookingConfirmation() {
  const navigate = useNavigate()
  const location = useLocation()
  const { showSuccess } = useNotifications()
  const haptics = useHaptics()
  
  const [showCalendarPrompt, setShowCalendarPrompt] = useState(false)

  const settings = usePublicSettings()
  const money = useMoney()

  // Booking details from navigation state; booking = the server's result
  const bookingData = location.state || {}
  const booking = bookingData.booking || {}
  const isAsap = !!booking.asap || bookingData.at === 'asap'
  const asapMinutes = settings?.['booking.asap_lead_minutes']
  const reminderMinutes = settings?.['reminders.before_visit_minutes']
  const jobMinutes = settings?.['booking.default_job_duration_minutes']
  const timeZone = settings?.['market.timezone']
  const phone = bookingData.professional?.phone || null

  useEffect(() => {
    // Show calendar prompt after a delay
    const timer = setTimeout(() => {
      setShowCalendarPrompt(true)
    }, 2000)

    return () => clearTimeout(timer)
  }, [])

  const handleAddToCalendar = () => {
    haptics.light()
    
    // Calendar event from the booked start and the job length setting
    if (!booking.scheduledStart || jobMinutes == null) return
    const startDate = new Date(booking.scheduledStart)
    const endDate = new Date(startDate.getTime() + jobMinutes * 60000)

    const calendarData = {
      title: `${bookingData.professional?.company || settings?.['onboarding.brand_name'] || ''} - Service Appointment`,
      start: startDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z',
      end: endDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z',
      description: `Service with ${bookingData.professional?.name || ''}\nBooking ID: ${bookingData.bookingId}${phone ? `\nPhone: ${phone}` : ''}`,
      location: addressLine(bookingData.address) || booking.address?.address || ''
    }

    // Create Google Calendar URL
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(calendarData.title)}&dates=${calendarData.start}/${calendarData.end}&details=${encodeURIComponent(calendarData.description)}&location=${encodeURIComponent(calendarData.location)}`
    
    window.open(googleCalendarUrl, '_blank')
    showSuccess('Calendar event created!')
    setShowCalendarPrompt(false)
  }

  const handleCall = () => {
    haptics.light()
    if (phone) window.location.href = `tel:${phone}`
  }

  const handleMessage = () => {
    haptics.light()
    if (phone) window.location.href = `https://wa.me/${phone.replace(/\D/g, '')}`
  }

  const handleShare = async () => {
    haptics.light()
    
    const shareData = {
      title: 'Service Booking Confirmation',
      text: `I've booked a service with ${bookingData.professional?.name} for ${visitLabel}. Booking ID: ${bookingData.bookingId}`,
      url: window.location.origin
    }

    if (navigator.share) {
      try {
        await navigator.share(shareData)
      } catch (error) {
        console.log('Share cancelled')
      }
    } else {
      // Fallback to copying to clipboard
      navigator.clipboard.writeText(`${shareData.text} - ${shareData.url}`)
      showSuccess('Booking details copied to clipboard!')
    }
  }

  const visitLabel = booking.scheduledStart
    ? new Date(booking.scheduledStart).toLocaleString('en-US', { timeZone, weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : formatYmd(bookingData.date)

  const getNextSteps = () => {
    const steps = [
      `${bookingData.professional?.company || 'The professional'} has been sent your booking and will confirm it on WhatsApp`,
      "We'll message you as soon as they confirm"
    ]
    if (isAsap) {
      steps.push(asapMinutes != null ? `The visit is targeted within ${asapMinutes} minutes — keep your phone nearby` : 'Keep your phone nearby')
    } else if (reminderMinutes != null) {
      steps.push(`You'll get a reminder ${reminderMinutes} minutes before the visit`)
    }
    steps.push('Prepare the service area and ensure easy access')
    return steps
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="px-4 py-8 space-y-6">
        {/* Success Header */}
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Booking Sent!</h1>
          <p className="text-slate-600">Waiting for the professional to confirm</p>
          <div className="mt-4 px-4 py-2 bg-slate-100 rounded-xl inline-block">
            <span className="text-sm font-medium text-slate-900">Booking ID: {bookingData.bookingId}</span>
          </div>
        </div>

        {/* Booking Details */}
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <h3 className="font-semibold text-slate-900 mb-4">Booking Details</h3>
          
          {/* Professional */}
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-100">
            <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-slate-600">
                {bookingData.professional?.name?.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
            <div className="flex-1">
              <div className="font-medium text-slate-900">{bookingData.professional?.name}</div>
              <div className="text-sm text-slate-600">{bookingData.professional?.company}</div>
            </div>
          </div>

          {/* Date & Time */}
          <div className="space-y-3 mb-4 pb-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <CalendarIcon className="text-slate-500" />
              <div>
                <div className="font-medium text-slate-900">
                  {isAsap ? 'Emergency Service' : visitLabel}
                </div>
                <div className="text-sm text-slate-600">
                  {isAsap ? (asapMinutes != null ? `Visit targeted within ${asapMinutes} minutes` : 'As soon as possible') : 
                   bookingData.urgency === 'today' ? 'Same day service' : 'Scheduled appointment'}
                </div>
              </div>
            </div>
            
            {!isAsap && bookingData.timeDisplay && (
              <div className="flex items-center gap-3">
                <ClockIcon className="text-slate-500" />
                <div>
                  <div className="font-medium text-slate-900">{bookingData.timeDisplay || bookingData.time}</div>
                  {bookingData.rate && <div className="text-sm text-slate-600">{bookingData.rate}</div>}
                </div>
              </div>
            )}
          </div>

          {/* Address */}
          <div className="flex items-start gap-3 mb-4 pb-4 border-b border-slate-100">
            <MapPinIcon className="text-slate-500 mt-0.5" />
            <div>
              <div className="font-medium text-slate-900">{bookingData.address?.label}</div>
              <div className="text-sm text-slate-600">
                {addressLine(bookingData.address) || booking.address?.address}
              </div>
            </div>
          </div>

          {/* Service Description */}
          {bookingData.serviceDescription && (
            <div className="mb-4 pb-4 border-b border-slate-100">
              <div className="font-medium text-slate-900 mb-1">Service Description</div>
              <div className="text-sm text-slate-600">{bookingData.serviceDescription}</div>
            </div>
          )}

          {/* Special Instructions */}
          {bookingData.specialInstructions && (
            <div className="mb-4 pb-4 border-b border-slate-100">
              <div className="font-medium text-slate-900 mb-1">Special Instructions</div>
              <div className="text-sm text-slate-600">{bookingData.specialInstructions}</div>
            </div>
          )}

          {/* Cost: the fee booked, in the currency the server booked it in */}
          <div>
            <div className="font-medium text-slate-900 mb-1">Call-out fee</div>
            <div className="text-lg font-bold text-slate-900">{money(booking.callOutFee, { currency: booking.currency })}</div>
            {pricedForText(booking.quote) && <div className="text-xs text-slate-500">Priced for: {pricedForText(booking.quote)}</div>}
            <div className="text-xs text-slate-500">
              {callOutCreditText(booking.callOutDeductible ?? booking.quote?.callOut?.deductible)}. The repair is quoted after the inspection.
            </div>
          </div>
        </div>

        <PaymentInstructions text={booking.paymentInstructions ?? booking.quote?.payment?.instructions} />

        {/* Next Steps */}
        <div className="bg-blue-50 rounded-2xl p-5">
          <h3 className="font-semibold text-slate-900 mb-3">What happens next?</h3>
          <div className="space-y-2">
            {getNextSteps().map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-200 text-blue-800 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
                  {index + 1}
                </div>
                <div className="text-sm text-slate-700">{step}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Professional */}
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <h3 className="font-semibold text-slate-900 mb-3">Contact Professional</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleCall}
              disabled={!phone}
              title={phone ? undefined : 'Not available yet'}
              className="disabled:opacity-40 flex items-center justify-center gap-2 py-3 px-4 border border-slate-300 rounded-xl text-slate-700 font-medium hover:bg-slate-50 touch-manipulation"
            >
              <PhoneIcon />
              Call
            </button>
            <button
              onClick={handleMessage}
              disabled={!phone}
              title={phone ? undefined : 'Not available yet'}
              className="disabled:opacity-40 flex items-center justify-center gap-2 py-3 px-4 border border-slate-300 rounded-xl text-slate-700 font-medium hover:bg-slate-50 touch-manipulation"
            >
              <MessageCircleIcon />
              Message
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <h3 className="font-semibold text-slate-900 mb-3">Quick Actions</h3>
          <div className="space-y-3">
            {!isAsap && (
              <button
                onClick={handleAddToCalendar}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 touch-manipulation"
              >
                <CalendarIcon />
                Add to Calendar
              </button>
            )}
            
            <button
              onClick={handleShare}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 touch-manipulation"
            >
              <ShareIcon />
              Share Booking Details
            </button>
            
            <button
              onClick={() => navigate('/orders')}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 touch-manipulation"
            >
              <DownloadIcon />
              Track Order Status
            </button>
          </div>
        </div>

        {/* Calendar Prompt */}
        {showCalendarPrompt && !isAsap && (
          <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <CalendarIcon className="text-green-600 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-green-900 mb-1">Don't forget your appointment!</div>
                <div className="text-sm text-green-700 mb-3">
                  Add this booking to your calendar so you don't miss it.
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleAddToCalendar}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg font-medium hover:bg-green-700 touch-manipulation"
                  >
                    Add to Calendar
                  </button>
                  <button
                    onClick={() => setShowCalendarPrompt(false)}
                    className="px-3 py-1.5 text-green-700 text-sm font-medium touch-manipulation"
                  >
                    Maybe Later
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Actions */}
      <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 safe-area-inset">
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/')}
            className="flex-1 py-3 border border-slate-300 text-slate-700 rounded-xl font-medium hover:bg-slate-50 touch-manipulation"
          >
            Back to Home
          </button>
          <button
            onClick={() => navigate('/orders')}
            className="flex-1 py-3 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 touch-manipulation"
          >
            Track Order
          </button>
        </div>
      </div>
    </div>
  )
}
